import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { FileText, Upload, Download, Trash2, Loader2, Paperclip } from 'lucide-react';
import { toast } from 'sonner';
import { saveBlob } from '../lib/download';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const formatTaille = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
};

export const downloadSupport = async (support) => {
  try {
    const res = await axios.get(`${API_URL}/supports/${support.id}/download`, { responseType: 'blob' });
    saveBlob(res.data, support.filename);
  } catch {
    toast.error('Erreur lors du téléchargement');
  }
};

export const ModuleSupportsSection = ({ moduleId, canManage = true }) => {
  const [supports, setSupports] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const fetchSupports = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/modules/${moduleId}/supports`);
      setSupports(res.data);
    } catch { /* silencieux */ }
  }, [moduleId]);

  useEffect(() => { fetchSupports(); }, [fetchSupports]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 25 Mo)');
      e.target.value = '';
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(`${API_URL}/modules/${moduleId}/supports`, formData);
      toast.success('Support ajouté');
      fetchSupports();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'upload");
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async (support) => {
    try {
      await axios.delete(`${API_URL}/supports/${support.id}`);
      toast.success('Support supprimé');
      setSupports(prev => prev.filter(s => s.id !== support.id));
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <div className="flex items-start gap-3" data-testid="module-supports-section">
      <div className="w-10 h-10 rounded-lg bg-sky-100 flex items-center justify-center flex-shrink-0">
        <Paperclip className="w-5 h-5 text-sky-600" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-medium text-slate-500">Supports pédagogiques ({supports.length})</p>
          {canManage && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.odp,.txt,.csv,.png,.jpg,.jpeg,.gif,.webp"
                onChange={handleUpload}
                data-testid="input-upload-support"
              />
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-7 text-xs gap-1.5"
                disabled={uploading}
                onClick={() => fileInputRef.current?.click()}
                data-testid="btn-upload-support"
              >
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                {uploading ? 'Envoi...' : 'Ajouter'}
              </Button>
            </>
          )}
        </div>
        {supports.length === 0 ? (
          <p className="text-xs text-slate-400">Aucun support pour ce module</p>
        ) : (
          <div className="space-y-1.5">
            {supports.map(s => (
              <div key={s.id} className="flex items-center gap-2 p-2 bg-sky-50 rounded-lg border border-sky-100" data-testid={`support-item-${s.id}`}>
                <FileText className="w-4 h-4 text-sky-600 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{s.filename}</p>
                  <p className="text-[10px] text-slate-400">
                    {formatTaille(s.taille)} — {s.uploaded_by_nom} — {s.uploaded_at ? new Date(s.uploaded_at).toLocaleDateString('fr-FR') : ''}
                  </p>
                </div>
                <Button type="button" size="icon" variant="ghost" className="h-7 w-7 text-sky-700" onClick={() => downloadSupport(s)} data-testid={`btn-download-support-${s.id}`}>
                  <Download className="w-3.5 h-3.5" />
                </Button>
                {canManage && (
                  <Button type="button" size="icon" variant="ghost" className="h-7 w-7 text-red-500 hover:text-red-700" onClick={() => handleDelete(s)} data-testid={`btn-delete-support-${s.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
